/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState } from 'react';
import { useCheckoutPricing } from '@recurly/react-recurly';
import { datadogLogs } from '@datadog/browser-logs';

import type { GtbOrderSummaryProps } from './GtbOrderSummaryTypes';
import useBoxName from '../../../hooks/useBoxName';
import {
  createPricingObject,
  createEmptyRecurlyError,
  buildAddonsCode,
  getSubscriptionName,
  // isCallSuccessful,
  // getParamFromUrl,
} from './helpers';
import { getTaxRate, getExchangeRate } from '../../../services';

import styles from './GtbOrderSummary.module.css';
import countryCodes from '../../../utils/countryCodes';
import { ShippingCountry } from '../../../services/tax';

export const PRICE_GBP_NOTE =
  // eslint-disable-next-line max-len
  'Please note that the price in GBP is an estimate based on an average of exchange rates calculated from financial data providers and banks, including the European Central Bank. Exchange rates are pulled once daily and may vary. Your actual charge will be in US dollars and is subject to the foreign exchange rate at the time of your purchase and foreign transaction fees applied by your payment provider.';
const GtbOrderSummary: React.FunctionComponent<GtbOrderSummaryProps> = ({
  prospect,
  setProspect,
  PromoGiftCard,
  CheckoutDisclaimer,
  disclaimerPriceIncrease,
  customize,
}) => {
  const { boxName } = useBoxName();
  const { selectedProduct } = customize;
  const { shipZip, shipCountry, shipState, planCode, promoCode = '' } = prospect;
  const [taxRate, setTaxRate] = useState<string>('0');
  const [exchangeRate, setExchangeRate] = useState(0);

  // TO DO: put this logic back and remove it from gtb
  // useEffect(() => {
  //   const urlCouponCode = getParamFromUrl(COUPON_CODE);
  //   if (urlCouponCode && !promoCode) {
  //     setPromoCode(urlCouponCode);
  //     setProspect({ ...prospect, promoCode: urlCouponCode });
  //   }
  // }, []);

  const updatePromoCode = (code: string) => {
    setProspect({ name: 'promoCode', value: code });
  };

  // get tax logic
  useEffect(() => {
    if (shipZip && shipCountry) {
      Promise.resolve(
        getTaxRate(shipCountry as ShippingCountry, shipZip, planCode),
      )
        .then((data) => {
          if (data && data.tax_rate) {
            setTaxRate(data.tax_rate);
            setProspect({
              name: 'shipZipError',
              value: null,
            });
          }
        })
        .catch((taxError: any) => {
          setTaxRate('0');
          setProspect({ name: 'shipZipError', value: taxError?.errors[0].msg });
          datadogLogs.logger.error(
            '[GtbOrderSummary]: getTaxRate',
            {},
            Error(JSON.stringify(taxError.errors)),
          );
        });
    }
  }, [shipZip, shipCountry, planCode]);

  useEffect(() => {
    const exchangeRateHasNotBeenSet = exchangeRate === 0 && shipCountry === countryCodes.GB;
    if (exchangeRateHasNotBeenSet) {
      Promise.resolve(
        getExchangeRate().then((rate) => {
          setExchangeRate(rate);
        })
      );
    }
  }, [shipCountry, exchangeRate]);

  // Regarding @ts-ignore comments on this file, this is just a temporary fix
  // until Recurly fix the setCheckoutPricing type
  // Referer to this ticket comments on Jira: https://fffptc.atlassian.net/browse/WEB-1973
  // And this thread on Slack: https://fabfitfun.slack.com/archives/C03A0TXD7GA/p1693002839502069
  const [recurlyError, setRecurlyError] = useState(createEmptyRecurlyError());
  const [{ price }, setCheckoutPricing] = useCheckoutPricing(
    // eslint-disable-next-line
    // @ts-ignore
    createPricingObject({
      planCode,
      tax: 0,
      addonCode: 'US-ALL',
      promoCode,
    }),
    setRecurlyError
  );

  const [addons, setAddons] = useState([]);

  useEffect(() => {
    if (!price.now) return;
    setRecurlyError(createEmptyRecurlyError());
    setCheckoutPricing(
      // eslint-disable-next-line
      // @ts-ignore
      createPricingObject({
        planCode,
        tax: parseFloat(price.now.taxes),
        addonCode: buildAddonsCode({ addons, shipState, shipCountry }),
        promoCode,
      })
    );
  }, [planCode, shipState, shipCountry, promoCode]);

  useEffect(() => {
    if (!price.now || !price.now.subtotal) return;
    setRecurlyError(createEmptyRecurlyError());
    const tax: number = parseFloat(price.now.subtotal) * parseFloat(taxRate);
    setCheckoutPricing(
      // eslint-disable-next-line
      // @ts-ignore
      createPricingObject({
        planCode,
        tax,
        addonCode: buildAddonsCode({ addons, shipState, shipCountry }),
        promoCode,
      })
    );
  }, [taxRate, price.now?.subtotal]);

  useEffect(() => {
    if (price.now?.discount) {
      setProspect({ name: 'discount', value: discount });
    }
  }, [price.now?.discount]);

  // Need the total for the purchase event upon successful registration
  useEffect(() => {
    if (price.now?.total) {
      setProspect({ name: 'total', value: total });
    }
  }, [price.now?.total]);

  if (!price.now) return null;

  const { taxes, total, items, discount, giftCard } = price.now;

  // exchange rate
  const priceInGBP = (parseFloat(total) * exchangeRate).toFixed(2);

  const getTaxText = () => {
    switch (shipCountry) {
      case 'GB':
        return 'VAT & Import Duties';
      case 'CA':
        return 'Taxes (Import/Border)';
      default:
        return 'Tax';
    }
  };

  return (
    <div className={styles.orderSummaryContainer}>
      <div className={styles.orderSummary}>
        <div className={styles.orderSummaryTitleWrapper}>
          <h4 className={styles.orderSummaryTitle}>Order Summary</h4>
        </div>
        <div className={styles.orderSection}>
          <div className={styles.orderRow}>
            <p className={styles.orderText}>{getSubscriptionName(planCode)}</p>
            <p className={styles.orderText}>{items[0]?.plan}</p>
          </div>
          <div className={styles.orderRow}>
            {boxName && (
              <p className={styles.orderText}>
                {'1st Box - '}
                <b>{boxName}</b>
              </p>
            )}
          </div>
        </div>
        <div className={styles.hr} />
        <div className={styles.orderSection}>
          <div className={styles.orderRow}>
            <p className={styles.orderText}>
              {shipCountry === countryCodes.GB ? 'P&P (Postage and Packaging)*' : 'Shipping*'}
            </p>
            <p className={`${styles.orderText} ${styles.orderTextRed}`}>{items[0]?.addons}</p>
          </div>
        </div>
        <div className={styles.hr} />
        {!!PromoGiftCard && (
          <PromoGiftCard
            promoCode={promoCode}
            setPromoCode={updatePromoCode}
            discount={discount}
            giftCard={giftCard}
            recurlyError={recurlyError}
          />
        )}
        <div className={styles.hr} />
        <div className={styles.orderSection}>
          <div className={styles.orderRow}>
            <p className={styles.orderText}>{getTaxText()}</p>
            <p className={styles.orderText}>${taxes}</p>
          </div>
        </div>
        <div className={styles.hr} />
        <div className={styles.orderSection}>
          <div className={styles.orderRow}>
            <p className={styles.orderText}>
              <b>Total</b>
            </p>
            <p className={styles.orderText} data-testid="order-summary-price-text">
              <b>${total}</b>
            </p>
          </div>
        </div>
        {shipCountry === 'GB' && (
          <div className={styles.exchangeRate}>
            <div className={styles.exchangeRatePrice}>
              <span className={styles.exchangeRatePriceTitle}>Estimated Price in GBP</span>
              <span className={styles.exchangeRatePriceValue}>
                {`(£${String(priceInGBP)} GBP)`}
              </span>
            </div>
            <p className={styles.exchangeRateDisclaimer}>{PRICE_GBP_NOTE}</p>
          </div>
        )}
        {shipCountry === 'GB' && <div className={styles.hr} />}
        <p className={styles.orderLegalTextLight}>
          *Free shipping only applicable in the contiguous US.
        </p>
        {!!CheckoutDisclaimer && <CheckoutDisclaimer flag={disclaimerPriceIncrease} />}
      </div>
    </div>
  );
};

export default GtbOrderSummary;
